import React, { useMemo, useState } from 'react';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Radio, Search, Users, MapPin, ArrowRight, Shield, Home, Eye } from 'lucide-react';
import { getSystemTypeLabel } from '@/lib/systemTypeConstants';
import { getSystemTypeIcon } from '@/lib/systemTypeIcons';

const SISTEMAS_RELEVANTES = ['intrusión', 'pci_deteccion_incendios'];

const ClientesAbonados: React.FC = () => {
  const navigate = useNavigate();
  const { perfil } = useAuth();
  const { clientes, sedes, sistemas, isLoading, setClienteActual } = useSecurityContext();
  const [searchTerm, setSearchTerm] = useState('');

  // Clientes con sistemas relevantes y sedes con abonado
  const clientesFiltrados = useMemo(() => {
    const clientesIdsConSistemaRelevante = new Set<string>();
    const sistemasPorCliente = new Map<string, Set<string>>();

    for (const sistema of sistemas) {
      if (!sistema?.cliente_id || !sistema?.tipo) continue;
      if (sistema.activo === false) continue;
      if (SISTEMAS_RELEVANTES.includes(sistema.tipo)) {
        clientesIdsConSistemaRelevante.add(sistema.cliente_id);
      }
      const setTipos = sistemasPorCliente.get(sistema.cliente_id) ?? new Set<string>();
      setTipos.add(sistema.tipo);
      sistemasPorCliente.set(sistema.cliente_id, setTipos);
    }

    const sedesConAbonadoPorCliente = new Map<string, typeof sedes>();
    for (const sede of sedes) {
      if (!sede?.cliente_id || !sede?.activa) continue;
      const tieneAbonado =
        (sede.numero_abonado && String(sede.numero_abonado).trim() !== '') ||
        ((sede as any).numero_abonado_incendio && String((sede as any).numero_abonado_incendio).trim() !== '');
      if (!tieneAbonado) continue;
      const lista = sedesConAbonadoPorCliente.get(sede.cliente_id) ?? [];
      lista.push(sede);
      sedesConAbonadoPorCliente.set(sede.cliente_id, lista);
    }

    const result = clientes
      .filter(cliente => (cliente as any).activo !== false)
      .filter(cliente => clientesIdsConSistemaRelevante.has(cliente.id))
      .filter(cliente => (sedesConAbonadoPorCliente.get(cliente.id)?.length ?? 0) > 0)
      .map(cliente => ({
        cliente,
        sedesConAbonado: sedesConAbonadoPorCliente.get(cliente.id) ?? [],
        tiposSistema: sistemasPorCliente.get(cliente.id) ?? new Set<string>(),
      }));

    if (!searchTerm.trim()) return result;

    const q = searchTerm.toLowerCase().trim();
    return result.filter(({ cliente, sedesConAbonado }) => {
      if (cliente.nombre.toLowerCase().includes(q)) return true;
      for (const sede of sedesConAbonado) {
        const abonado = String(sede.numero_abonado || '').toLowerCase();
        const abonadoIncendio = String((sede as any).numero_abonado_incendio || '').toLowerCase();
        if (abonado.includes(q)) return true;
        if (abonadoIncendio.includes(q)) return true;
        if ((sede.nombre || '').toLowerCase().includes(q)) return true;
        if ((sede.direccion || '').toLowerCase().includes(q)) return true;
        if ((sede.poblacion || '').toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [clientes, sedes, sistemas, searchTerm]);

  const handleClienteClick = (cliente: any) => {
    setClienteActual(cliente);
    navigate(`/cliente/${cliente.id}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4 md:p-6">
        <div className="max-w-6xl mx-auto mobile-spacing">
          <Card className="border border-border/50">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent mb-4" />
              <p className="text-muted-foreground">Cargando clientes y abonados...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4 md:p-6">
      <div className="max-w-6xl mx-auto mobile-spacing space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground mobile-text flex items-center gap-3">
              <Radio className="h-7 w-7 text-primary" />
              Clientes con Abonado
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Clientes con sistemas de intrusión o detección de incendios que disponen de número de abonado.
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => navigate('/clientes')}
              className="gap-2 w-full sm:w-auto"
            >
              <Users className="h-4 w-4" />
              Ver clientes
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por cliente, abonado, dirección o población..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Results count */}
        {clientesFiltrados.length > 0 && (
          <p className="text-sm text-muted-foreground">
            Mostrando {clientesFiltrados.length} cliente{clientesFiltrados.length !== 1 ? 's' : ''} con abonado
          </p>
        )}

        {/* Lista */}
        <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {clientesFiltrados.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Radio className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No hay clientes con abonado</h3>
                <p className="text-muted-foreground text-center mb-4 max-w-md">
                  {searchTerm
                    ? `No se encontraron resultados para "${searchTerm}".`
                    : 'Para aparecer aquí, un cliente debe tener un sistema de intrusión o detección de incendios y al menos una sede con número de abonado registrado.'}
                </p>
                {searchTerm && (
                  <Button variant="outline" onClick={() => setSearchTerm('')}>
                    Limpiar búsqueda
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            clientesFiltrados.map(({ cliente, sedesConAbonado, tiposSistema }) => (
              <Card
                key={cliente.id}
                className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-primary/50 group flex flex-col"
                onClick={() => handleClienteClick(cliente)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="flex items-center gap-2 flex-wrap text-lg">
                      <Users className="h-5 w-5 text-primary shrink-0" />
                      <span className="break-words">{cliente.nombre}</span>
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClienteClick(cliente);
                      }}
                      title="Ver ficha del cliente"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                  <CardDescription className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 shrink-0" />
                    {cliente.direccion || 'Sin dirección principal'}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4 flex-1">
                  {/* Badges de sistemas relevantes */}
                  <div className="flex flex-wrap gap-1.5">
                    {SISTEMAS_RELEVANTES.filter(tipo => tiposSistema.has(tipo)).map(tipo => {
                      const Icon = getSystemTypeIcon(tipo);
                      return (
                        <Badge key={tipo} variant="secondary" className="text-[11px] gap-1 font-normal">
                          <Icon className="h-3 w-3" />
                          {getSystemTypeLabel(tipo)}
                        </Badge>
                      );
                    })}
                  </div>

                  {/* Sedes con abonado */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <Home className="h-3 w-3" />
                      Sedes con abonado
                    </p>
                    <div className="space-y-2">
                      {sedesConAbonado.map(sede => (
                        <div
                          key={sede.id}
                          className="rounded-md border bg-muted/30 p-2.5 text-sm"
                        >
                          <div className="font-medium flex items-center justify-between gap-2 flex-wrap">
                            <span className="truncate">{sede.nombre}</span>
                            <div className="flex gap-1 flex-wrap shrink-0">
                              {sede.numero_abonado && (
                                <Badge variant="outline" className="text-[11px] font-mono" title="Abonado intrusión">
                                  Intrusión {sede.numero_abonado}
                                </Badge>
                              )}
                              {(sede as any).numero_abonado_incendio && (
                                <Badge variant="outline" className="text-[11px] font-mono" title="Abonado incendios">
                                  Incendio {(sede as any).numero_abonado_incendio}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                            {sede.direccion && <span>{sede.direccion}</span>}
                            {sede.poblacion && <span>{sede.poblacion}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-2 flex items-center text-xs text-muted-foreground gap-1 group-hover:text-primary transition-colors">
                    <span>Ver ficha del cliente</span>
                    <ArrowRight className="h-3 w-3" />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientesAbonados;
